/**
 * CR500 Phase 2B: Inline Expand Presentation Mode
 * 
 * Renders the deal card with expandable inline details below.
 * Scaffolded - full implementation renders same blocks as flip.
 */

import React from "react";
import { ChevronDown, ChevronUp } from "lucide-react";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import type { Deal } from "@/lib/types/deals";
import type { ResolvedDealCardSpecV2 } from "@/lib/deal-cards/specV2/types";
import type { 
  DetailsEntryConfig, 
  InlineExpandPresentationConfig,
} from "@/lib/layout-studio/detailsPresentationTypes";
import { DEFAULT_DETAILS_ENTRY, DEFAULT_INLINE_EXPAND_CONFIG } from "@/lib/layout-studio/detailsPresentationTypes";
import { DetailsBlocksRenderer } from "./DetailsBlocksRenderer";
import { cn } from "@/lib/utils";

// ================================================================
// TYPES
// ================================================================

export interface InlineExpandPresentationProps {
  /** The deal being displayed */
  deal: Deal & { id?: string };
  /** The resolved v2 spec */
  spec: ResolvedDealCardSpecV2;
  /** Admin context */
  isAdmin?: boolean;
  /** Whether expanded */
  isExpanded?: boolean;
  /** Toggle handler */
  onToggle?: () => void;
  /** Expand configuration */
  config?: InlineExpandPresentationConfig;
  /** Entry button configuration */
  entryConfig?: DetailsEntryConfig;
  /** The card content */
  children: React.ReactNode;
}

// ================================================================
// COMPONENT
// ================================================================

export function InlineExpandPresentation({
  deal,
  spec,
  isAdmin = false,
  isExpanded = false,
  onToggle,
  config = DEFAULT_INLINE_EXPAND_CONFIG,
  entryConfig = DEFAULT_DETAILS_ENTRY,
  children,
}: InlineExpandPresentationProps) {
  // Internal state if not controlled
  const [internalExpanded, setInternalExpanded] = React.useState(false);
  
  const expanded = onToggle !== undefined ? isExpanded : internalExpanded;
  const handleToggle = onToggle ?? (() => setInternalExpanded(!internalExpanded));
  
  const handleTriggerClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    handleToggle();
  };
  
  return (
    <Collapsible open={expanded} onOpenChange={handleToggle}>
      {/* Card with trigger */}
      <div className="relative">
        {children}
        
        {/* Entry button */}
        <CollapsibleTrigger asChild>
          <button
            onClick={handleTriggerClick}
            className={cn(
              "absolute z-10 px-3 py-1.5 text-xs font-medium rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 flex items-center gap-1",
              entryConfig.variant === "primary" && "bg-primary text-primary-foreground hover:bg-primary/90 focus:ring-primary",
              entryConfig.variant === "secondary" && "bg-secondary text-secondary-foreground hover:bg-secondary/90 focus:ring-secondary",
              entryConfig.variant === "ghost" && "bg-background/80 text-foreground hover:bg-muted focus:ring-muted",
              entryConfig.position === "bottom-right" && "bottom-3 right-3",
              entryConfig.position === "bottom-left" && "bottom-3 left-3",
              entryConfig.position === "bottom-center" && "bottom-3 left-1/2 -translate-x-1/2",
            )}
            aria-label={expanded ? "Hide details" : "Show details"}
            aria-expanded={expanded}
          >
            {entryConfig.label}
            {expanded ? (
              <ChevronUp className="h-3 w-3" />
            ) : (
              <ChevronDown className="h-3 w-3" />
            )}
          </button>
        </CollapsibleTrigger>
      </div>
      
      {/* Expandable content */}
      <CollapsibleContent 
        className={cn(
          "overflow-hidden",
          "data-[state=open]:animate-collapsible-down data-[state=closed]:animate-collapsible-up"
        )}
        style={{
          animationDuration: `${config.animationDuration || 300}ms`,
        }}
      >
        <div className="mt-2 bg-card border border-border rounded-lg shadow-sm p-4">
          <DetailsBlocksRenderer
            deal={deal}
            spec={spec}
            isAdmin={isAdmin}
            mode="expand"
          />
        </div>
      </CollapsibleContent>
    </Collapsible>
  );
}

export default InlineExpandPresentation;
